import React from 'react';
import SubjectContext from './SubjectContext';
import {
  formatAlertTypeLabel,
  getAlertDisplayTimestamp,
} from '../utils/alertUtils';

/**
 * AlertRow Component - Individual table row for an alert
 * Compact representation with actions
 */
function AlertRow({ alert, isSelected, onSelectChange, onViewDevice, onViewComplaint, onUpdateStatus }) {

  /**
   * Get emoji icon for severity level
   */
  const getSeverityIcon = (severity) => {
    const icons = {
      critical: '🔴',
      warning: '⚠️',
      info: 'ℹ️',
    };
    return icons[severity] || '📌';
  };

  /**
   * Get emoji icon for alert type
   * Maps backend alert types (ppm_overdue, calibration_due_soon, etc.) to icons
   */
  const getTypeIcon = (type) => {
    if (!type) return '📌';

    // Map base types to icons
    const baseTypeIcons = {
      ppm: '🔧',
      maintenance: '🔧',
      calibration: '⚖️',
      warranty: '🛡️',
      contract: '📜',
      compliance: '📋',
      complaint: '📣',
    };

    // Extract base type from full type (e.g., 'ppm_overdue' → 'ppm')
    if (type.startsWith('device_transfer_')) {
      return '🚚';
    }

    const baseType = type.split('_')[0].toLowerCase();
    return baseTypeIcons[baseType] || '📌';
  };

  /**
   * Get status badge display
   */
  const getStatusBadge = (status) => {
    const badges = {
      acknowledged: { icon: '✓', label: 'Acknowledged', color: '#10b981' },
      snoozed: { icon: '⏰', label: 'Snoozed', color: '#f59e0b' },
      archived: { icon: '📦', label: 'Archived', color: '#6c757d' },
      active: { icon: '●', label: 'Active', color: 'var(--dp-accent-primary)' },
    };
    return badges[status] || badges.active;
  };


  /**
   * Format date for display - shows full date with month name
   */
  const formatAlertDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  /**
   * Format datetime for display - returns both date and time
   */
  const formatAlertDateTime = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      }),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const statusBadge = getStatusBadge(alert.status);
  const displayTimestamp = getAlertDisplayTimestamp(alert);
  const displayDateTime = formatAlertDateTime(displayTimestamp);

  const handleAcknowledge = (e) => {
    e.stopPropagation();
    onUpdateStatus(alert.id, {
      status: 'acknowledged',
      acknowledged_at: new Date().toISOString(),
    });
  };

  const handleSnooze = (e) => {
    e.stopPropagation();
    const snoozedUntil = new Date();
    snoozedUntil.setDate(snoozedUntil.getDate() + 7);
    onUpdateStatus(alert.id, {
      status: 'snoozed',
      snoozed_until: snoozedUntil.toISOString(),
    });
  };

  const handleUnsnooze = (e) => {
    e.stopPropagation();
    onUpdateStatus(alert.id, {
      status: 'active',
      snoozed_until: null,
    });
  };

  const handleArchive = (e) => {
    e.stopPropagation();
    onUpdateStatus(alert.id, { status: 'archived' });
  };

  return (
    <>
      {/* Main Row */}
      <tr
        className={`alert-table-row alert-severity-${alert.severity} alert-status-${alert.status} ${isSelected ? 'selected' : ''}`}
      >
        {/* Checkbox Column */}
        <td className="alert-table-col-checkbox">
          <input
            type="checkbox"
            className="alert-checkbox"
            checked={isSelected}
            onChange={onSelectChange}
            onClick={(e) => e.stopPropagation()}
            title="Select this alert"
            aria-label={`Select alert: ${alert.title}`}
          />
        </td>

        {/* Severity Column */}
        <td className="alert-table-col-severity">
          <div className="alerts-severity-cell">
            <span className="alerts-severity-icon" title={`Severity: ${alert.severity}`}>
              {getSeverityIcon(alert.severity)}
            </span>
            <span className="alerts-severity-label">{alert.severity}</span>
          </div>
        </td>

        {/* Type Column - Phase 4.5: Now includes days_overdue indicator */}
        <td className="alert-table-col-type">
          <div className="type-cell">
            <span className="type-icon" title={`Type: ${alert.type} (${alert.type_display})`}>
              {getTypeIcon(alert.type)}
            </span>
            <div className="type-info">
              <span className="type-label" title={alert.type}>
                {formatAlertTypeLabel(alert.type)}
              </span>
              {alert.days_overdue > 0 && (
                <span
                  className="days-overdue-badge"
                  style={{
                    color: alert.days_overdue > 7 ? '#991b1b' : '#b45309',
                    backgroundColor: alert.days_overdue > 7 ? '#fee2e2' : '#fef3c7',
                    border: `1px solid ${alert.days_overdue > 7 ? '#fca5a5' : '#fcd34d'}`
                  }}
                  title={`${alert.days_overdue} days overdue`}
                >
                  {alert.days_overdue}d ⏱️
                </span>
              )}
            </div>
          </div>
        </td>

        <td className="alert-table-col-subject">
          <SubjectContext
            alert={alert}
            onViewDevice={onViewDevice}
            onViewComplaint={onViewComplaint}
          />
        </td>

        {/* Message Column - Full content */}
        <td className="alert-table-col-message">
          <span
            className="message-text"
            title={alert.message.replace(/<[^>]*>/g, '')}
            dangerouslySetInnerHTML={{ __html: alert.message }}
          />
        </td>

        {/* Status & Date Column - Stacked */}
        <td className="alert-table-col-status-date">
          <div className="status-date-stack">
            <div className="status-cell" style={{ color: statusBadge.color }}>
              <span className="status-icon">{statusBadge.icon}</span>
              <span className="status-label">{statusBadge.label}</span>
            </div>
            {alert.status === 'snoozed' && alert.snoozed_until && (
              <div
                className="snoozed-until-text"
                title={`Snoozed until ${new Date(alert.snoozed_until).toLocaleString()}`}
              >
                until {new Date(alert.snoozed_until).toLocaleDateString()}
              </div>
            )}
            <div className="alert-date-time-stack" title={displayTimestamp ? new Date(displayTimestamp).toLocaleString() : ''}>
              <div className="alert-date-text" style={{ fontWeight: '500', fontSize: '0.85rem', color: '#2c3e50' }}>
                {displayDateTime.date}
              </div>
              <div className="alert-time-text" style={{ fontWeight: '500', fontSize: '0.75rem', color: '#6c757d' }}>
                {displayDateTime.time}
              </div>
            </div>
          </div>
        </td>

        {onUpdateStatus && (
          <td className="alert-table-col-actions">
            <div className="actions-cell inline-actions">
              {alert.status === 'active' && (
                <button
                  type="button"
                  className="action-btn-inline action-acknowledge"
                  onClick={handleAcknowledge}
                  title="Acknowledge this alert"
                >
                  ✓ Ack
                </button>
              )}
              {(alert.status === 'active' || alert.status === 'acknowledged') && (
                <button
                  type="button"
                  className="action-btn-inline action-snooze"
                  onClick={handleSnooze}
                  title="Snooze this alert for 7 days"
                >
                  ⏰ Snooze
                </button>
              )}
              {alert.status === 'snoozed' && (
                <button
                  type="button"
                  className="action-btn-inline action-unsnooze"
                  onClick={handleUnsnooze}
                  title="Return this alert to Active"
                >
                  ↩ Active
                </button>
              )}
              {(alert.status === 'acknowledged' || alert.status === 'snoozed') && (
                <button
                  type="button"
                  className="action-btn-inline action-archive"
                  onClick={handleArchive}
                  title="Archive this alert"
                >
                  🗂️ Archive
                </button>
              )}
            </div>
          </td>
        )}
      </tr>

      {/* Detail Row - Hidden by default, can be expanded for more info */}
      {/* (Optional: can add expandable row for full details here) */}
    </>
  );
}

export default AlertRow;
